import { useEffect, useMemo, useState } from "react";
import { getDefaultCity } from "../lib/mara/cities";
import { fetchMaraDay, omanTodayISO } from "../lib/mara/fetcher";
import { toHijri } from "../lib/utils/hijriDate";
import type { Locale, UserSettings } from "../types/app";

export function useClock(locale: Locale, hijriOffset: UserSettings["hijriOffset"] = 0) {
  const [now, setNow] = useState(() => new Date());
  const [officialDate, setOfficialDate] = useState<{ gregorianAr: string; gregorianEn: string; hijriAr: string; hijriEn: string } | null>(null);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const omanIsoDate = omanTodayISO(now);

  useEffect(() => {
    const controller = new AbortController();
    const defaultCity = getDefaultCity();
    setOfficialDate(null);

    void fetchMaraDay(defaultCity.id, defaultCity.slug, omanIsoDate, controller.signal)
      .then((result) => {
        if (!result.data || controller.signal.aborted) return;
        setOfficialDate(result.data.officialDate);
      })
      .catch(() => {
        if (!controller.signal.aborted) setOfficialDate(null);
      });

    return () => controller.abort();
  }, [omanIsoDate]);

  return useMemo(() => {
    const dateLocale = locale === "ar" ? "ar-OM" : "en-US";
    const hijriResult = toHijri(now, hijriOffset);
    const officialGregorian = officialDate ? (locale === "ar" ? officialDate.gregorianAr : officialDate.gregorianEn) : "";
    const officialHijri = officialDate ? (locale === "ar" ? officialDate.hijriAr : officialDate.hijriEn) : "";
    const hijri = hijriOffset === 0 && officialHijri
      ? officialHijri
      : (locale === "ar" ? hijriResult.formatted : hijriResult.formattedEn);

    return {
      now,
      time: new Intl.DateTimeFormat(dateLocale, { hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(now),
      gregorian: officialGregorian || new Intl.DateTimeFormat(dateLocale, { weekday: "long", year: "numeric", month: "long", day: "numeric" }).format(now),
      hijri
    };
  }, [hijriOffset, locale, now, officialDate]);
}
